import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { useGetSubscriptionEventsQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import { adminUserDetailPath } from 'routes/paths';
import TimeRangeDropdown from './TimeRangeDropdown';
import {
  EVENT_LABELS,
  EVENT_TONES,
  PLAN_LABELS,
  SUBSCRIPTION_EVENT_COLUMNS,
  formatOptionalAdminDate,
  isMissingEndpointError,
  subscriptionEventDetailsLine,
} from './subscriptionEvents';
import './SubscriptionEventsTable.css';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const EMPTY_MESSAGE = 'No subscription events in this period.';

/**
 * Who the Revenue charts are about: the period's signups, cancellations and
 * trial starts / stops, newest first. The period is this card's own (each
 * Revenue chart has its own too). Rows render exactly as the endpoint returns
 * them; nothing is recounted or de-duplicated here.
 */
const SubscriptionEventsTable: React.FC<Props> = ({ period, onPeriodChange }) => {
  // The cursor belongs to the period it was issued for: a new period starts
  // again from the first page, in the same render that the period changes.
  const [paging, setPaging] = useState<{ period: AnalyticsPeriod; cursor?: string }>({ period });
  const cursor = paging.period === period ? paging.cursor : undefined;

  const { data, isLoading, isFetching, error } = useGetSubscriptionEventsQuery({ period, cursor });

  const endpointMissing = isMissingEndpointError(error);
  useEffect(() => {
    if (endpointMissing) {
      console.warn('[SubscriptionEventsTable] /admin/subscriptions/events is not on this backend yet; showing the empty state.');
    }
  }, [endpointMissing]);

  const events = endpointMissing ? [] : data?.events ?? [];
  const nextCursor = endpointMissing ? null : data?.nextCursor ?? null;

  let body: React.ReactNode;
  if (isLoading) {
    body = <div className="chart-loading">Loading...</div>;
  } else if (error && !endpointMissing) {
    body = (
      <div className="chart-error" data-testid="subscription-events-error">
        Failed to load data
      </div>
    );
  } else if (events.length === 0) {
    body = (
      <div className="admin-sub-events-empty" data-testid="subscription-events-empty">
        {EMPTY_MESSAGE}
      </div>
    );
  } else {
    body = (
      <>
        {/* Seven columns do not fit a half-width card: the table scrolls inside the card */}
        <div className="admin-sub-events-scroll" data-testid="subscription-events-scroll">
          <table className="admin-sub-events-table">
            <thead>
              <tr>
                {SUBSCRIPTION_EVENT_COLUMNS.map((column) => (
                  <th key={column} scope="col">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((event) => {
                const detailsLine = subscriptionEventDetailsLine(event.details);
                return (
                  <React.Fragment key={event.eventId}>
                    <tr
                      className={`admin-sub-events-row${detailsLine ? ' admin-sub-events-row--has-details' : ''}`}
                      data-testid="subscription-event-row"
                    >
                      <td>
                        <span className="admin-sub-events-chips">
                          {event.types.map((type) => (
                            <span
                              key={type}
                              className={`admin-sub-events-chip admin-sub-events-chip--${EVENT_TONES[type] ?? 'negative'}`}
                              data-testid="subscription-event-chip"
                            >
                              {EVENT_LABELS[type] ?? type}
                            </span>
                          ))}
                        </span>
                      </td>
                      <td>
                        <Link to={adminUserDetailPath(event.userId)} className="admin-sub-events-user-link">
                          {event.username}
                        </Link>
                      </td>
                      <td>{event.email}</td>
                      <td>{PLAN_LABELS[event.plan] ?? event.plan}</td>
                      <td className="admin-sub-events-date">{formatOptionalAdminDate(event.eventAt)}</td>
                      <td className="admin-sub-events-date">{formatOptionalAdminDate(event.eliteSince)}</td>
                      <td className="admin-sub-events-date">{formatOptionalAdminDate(event.lastSubscriptionEventAt)}</td>
                    </tr>
                    {detailsLine && (
                      <tr className="admin-sub-events-details" data-testid="subscription-event-details">
                        <td colSpan={SUBSCRIPTION_EVENT_COLUMNS.length}>{detailsLine}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {nextCursor && (
          <div className="admin-sub-events-more">
            <MagicButton
              label={isFetching ? 'Loading...' : 'Show more'}
              color="dark-grey"
              onPress={() => setPaging({ period, cursor: nextCursor })}
              isDisabled={isFetching}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="chart-card admin-sub-events-card" data-testid="subscription-events-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Subscription events</h2>
          <p className="chart-subtitle">Signups, cancellations, trials</p>
        </div>
        <div className="chart-header-right">
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>
      <div className="admin-sub-events-body">{body}</div>
    </div>
  );
};

export default SubscriptionEventsTable;
